import React, { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, IconButton, Typography, CircularProgress } from "@mui/material";
import {
  Close,
  ArrowBack,
  ArrowForward,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import { getFullResolutionUrls } from "../../DropboxFunctions";

const IMAGE_EXTENSIONS =
  /\.(jpe?g|png|gif|bmp|tiff?|webp|heic|heif)$/i;
const VIDEO_EXTENSIONS = /\.(mp4|mov|m4v|webm|avi|mts|m2ts)$/i;

const isImageEntry = (entry) => {
  const normalizedName = (entry?.name || "").toLowerCase();
  return Boolean(normalizedName && IMAGE_EXTENSIONS.test(normalizedName));
};

const isVideoEntry = (entry) => {
  const normalizedName = (entry?.name || "").toLowerCase();
  return Boolean(normalizedName && VIDEO_EXTENSIONS.test(normalizedName));
};

const StoryboardLightbox = ({
  isOpen,
  onClose,
  mediaItems = [],
  initialIndex = 0,
  thumbnails = {},
  onRequestMoreMedia,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [fullResSources, setFullResSources] = useState({});
  const [loadedHighRes, setLoadedHighRes] = useState({}); // Track which high-res images are fully loaded
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  // const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const totalItems = mediaItems.length;
  const currentItem = mediaItems[currentIndex] || null;
  const currentIsImage = currentItem && isImageEntry(currentItem);
  const currentIsVideo = currentItem && isVideoEntry(currentItem);

  // Add refs to track loading state and prevent duplicate requests
  const loadingRequestsRef = useRef(new Set());
  const loadedImagesRef = useRef(new Set());
  const debounceTimeoutRef = useRef(null);

  // Reset state when lightbox opens or image changes
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!totalItems && currentIndex !== 0) {
      setCurrentIndex(0);
    } else if (totalItems && currentIndex > totalItems - 1) {
      setCurrentIndex(totalItems - 1);
    }
  }, [totalItems, currentIndex]);

  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    // Reset high-res loaded state for new image to ensure thumbnail shows first
    if (currentIsImage && currentItem) {
      setLoadedHighRes((prev) => ({
        ...prev,
        [currentItem.path_lower]: false,
      }));
    }
  }, [currentIndex, mediaItems, currentIsImage, currentItem]);

  // Cleanup refs when lightbox closes
  useEffect(() => {
    if (!isOpen) {
      // Clear the tracking refs when lightbox closes
      loadingRequestsRef.current.clear();
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      // Keep loadedImagesRef to maintain cache across sessions
    }
  }, [isOpen]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Background load high-res images using new Image() approach
  const backgroundLoadHighRes = useCallback(async (itemsToLoad) => {
    if (!itemsToLoad.length) return;

    // Only process image entries
    const imageCandidates = itemsToLoad.filter(isImageEntry);

    // Filter out already loaded/loading images
    const imagesToActuallyLoad = imageCandidates.filter(
      (image) =>
        !loadedImagesRef.current.has(image.path_lower) &&
        !loadingRequestsRef.current.has(image.path_lower)
    );

    if (!imagesToActuallyLoad.length) return;

    // Mark all as loading
    imagesToActuallyLoad.forEach((image) => {
      loadingRequestsRef.current.add(image.path_lower);
    });

    try {
      // Get URLs from API first
      const fullResUrls = await getFullResolutionUrls(imagesToActuallyLoad);

      if (fullResUrls && Object.keys(fullResUrls).length > 0) {
        // Update URLs immediately (for src attribute)
        const newFullResImages = {};
        Object.entries(fullResUrls).forEach(([pathLower, directUrl]) => {
          if (directUrl) {
            newFullResImages[pathLower] = directUrl;
          }
        });
        setFullResSources((prev) => ({ ...prev, ...newFullResImages }));

        // Now load each image in background using new Image()
        Object.entries(fullResUrls).forEach(([pathLower, directUrl]) => {
          if (directUrl) {
            const img = new Image();
            img.onload = () => {
              // Mark as fully loaded only when image is ready
              setLoadedHighRes((prev) => ({ ...prev, [pathLower]: true }));
              loadedImagesRef.current.add(pathLower);
            };
            img.onerror = () => {
              console.error(`Failed to load high-res image: ${pathLower}`);
              loadingRequestsRef.current.delete(pathLower);
            };
            img.src = directUrl;
          }
        });
      }
    } catch (error) {
      console.error("Failed to get full resolution URLs:", error);
    } finally {
      // Remove from loading requests for URL fetching (actual image loading tracked separately)
      imagesToActuallyLoad.forEach((image) => {
        loadingRequestsRef.current.delete(image.path_lower);
      });
    }
  }, []);

  // Load image assets when navigating
  useEffect(() => {
    if (!isOpen || !mediaItems.length) return;

    const currentItem = mediaItems[currentIndex];
    if (!currentItem || !isImageEntry(currentItem)) {
      return;
    }

    const preloadRange = 1;
    const startIndex = Math.max(0, currentIndex - preloadRange);
    const endIndex = Math.min(mediaItems.length - 1, currentIndex + preloadRange);

    const imagesToPreload = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const candidate = mediaItems[i];
      if (candidate && isImageEntry(candidate)) {
        imagesToPreload.push(candidate);
      }
    }

    const otherImages = imagesToPreload.filter(
      (img) => img.path_lower !== currentItem.path_lower
    );

    backgroundLoadHighRes([currentItem]);

    if (otherImages.length > 0) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        backgroundLoadHighRes(otherImages);
      }, 300);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [currentIndex, isOpen, mediaItems, backgroundLoadHighRes]);

  // Auto-load more media when approaching unloaded items
  useEffect(() => {
    if (!isOpen || !onRequestMoreMedia) return;

    const currentItem = mediaItems[currentIndex];
    if (!currentItem) return;

    // Check if we're within 5 items of an unloaded thumbnail
    const lookAheadRange = 5;
    for (let i = 1; i <= lookAheadRange; i++) {
      const checkIndex = currentIndex + i;
      if (checkIndex >= mediaItems.length) break;

      const checkItem = mediaItems[checkIndex];
      if (checkItem && !thumbnails[checkItem.path_lower]) {
        // Found an unloaded item nearby - request more
        onRequestMoreMedia(checkIndex);
        break;
      }
    }
  }, [currentIndex, isOpen, mediaItems, thumbnails, onRequestMoreMedia]);

  // Ensure video items fetch direct URLs when required
  useEffect(() => {
    if (!isOpen || !mediaItems.length) return;
    const currentItem = mediaItems[currentIndex];
    if (!currentItem || !isVideoEntry(currentItem)) return;
    if (fullResSources[currentItem.path_lower]) return;

    let cancelled = false;
    (async () => {
      try {
        const urls = await getFullResolutionUrls([currentItem]);
        if (!cancelled && urls) {
          setFullResSources((prev) => ({ ...prev, ...urls }));
        }
      } catch (error) {
        console.error("Failed to load video preview:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentIndex, isOpen, mediaItems, fullResSources]);

  // Load high-res when zooming in for quality
  useEffect(() => {
    if (!isOpen || zoom <= 1) return;
    const currentItem = mediaItems[currentIndex];
    if (!currentItem || !isImageEntry(currentItem)) return;

    backgroundLoadHighRes([currentItem]);
  }, [zoom, currentIndex, isOpen, mediaItems, backgroundLoadHighRes]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // CMD+0 (Mac) or Ctrl+0 (Windows) to reset zoom
      if ((e.metaKey || e.ctrlKey) && e.key === "0") {
        e.preventDefault();
        setZoom(1);
        setPan({ x: 0, y: 0 });
        return;
      }

      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "+":
        case "=":
          zoomIn();
          break;
        case "-":
        case "_":
          zoomOut();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line
  }, [isOpen, currentIndex, mediaItems.length]);

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < mediaItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const zoomIn = () => {
    setZoom((prev) => Math.min(prev * 1.1, 5));
  };

  const zoomOut = () => {
    setZoom((prev) => {
      const newZoom = prev / 1.1;
      if (newZoom <= 1) {
        setPan({ x: 0, y: 0 });
        return 1;
      }
      return newZoom;
    });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - pan.x,
        y: e.clientY - pan.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  };

  // Touch/swipe handlers for mobile
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setTouchStart({ x: touch.clientX, y: touch.clientY });

      if (zoom > 1) {
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - pan.x,
          y: touch.clientY - pan.y,
        });
      }
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && isDragging && zoom > 1) {
      const touch = e.touches[0];
      setPan({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (e.changedTouches.length === 1) {
      const touch = e.changedTouches[0];
      // setTouchEnd({ x: touch.clientX, y: touch.clientY });

      // Only process swipe if not zoomed and not dragging
      if (zoom <= 1 && !isDragging) {
        const deltaX = touchStart.x - touch.clientX;
        const deltaY = Math.abs(touchStart.y - touch.clientY);

        // Horizontal swipe threshold
        if (Math.abs(deltaX) > 50 && deltaY < 100) {
          if (deltaX > 0) {
            // Swipe left - next image
            goToNext();
          } else {
            // Swipe right - previous image
            goToPrevious();
          }
        }
      }
    }

    setIsDragging(false);
  };

  if (!isOpen || !mediaItems.length) return null;

  const thumbnailUrl = thumbnails[currentItem?.path_lower];
  const fullResUrl = currentItem ? fullResSources[currentItem.path_lower] : null;
  const isHighResFullyLoaded =
    (currentItem && loadedHighRes[currentItem.path_lower]) || false;

  const shouldShowHighRes =
    currentIsImage && (isHighResFullyLoaded || (zoom > 1 && fullResUrl));
  const shouldShowThumbnail = currentIsImage && !shouldShowHighRes;

  const isImageLoading = currentIsImage && !thumbnailUrl && !fullResUrl;
  const isVideoLoading = currentIsVideo && !fullResUrl;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
              zIndex: 10001,
            }}
          >
            <Close />
          </IconButton>

          {/* Navigation Buttons */}
          {totalItems > 1 && (
            <>
              <IconButton
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                sx={{
                  position: "absolute",
                  left: 20,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                  "&:disabled": {
                    color: "rgba(255, 255, 255, 0.3)",
                  },
                  zIndex: 10001,
                }}
              >
                <ArrowBack />
              </IconButton>

              <IconButton
                onClick={goToNext}
                disabled={currentIndex === totalItems - 1}
                sx={{
                  position: "absolute",
                  right: 20,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                  "&:disabled": {
                    color: "rgba(255, 255, 255, 0.3)",
                  },
                  zIndex: 10001,
                }}
              >
                <ArrowForward />
              </IconButton>
            </>
          )}

          {/* Zoom Controls */}
          {currentIsImage && (
            <Box
              sx={{
                position: "absolute",
                bottom: 20,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 1,
                zIndex: 10001,
              }}
            >
              <IconButton
                onClick={zoomOut}
                disabled={zoom <= 1}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                  "&:disabled": {
                    color: "rgba(255, 255, 255, 0.3)",
                  },
                }}
              >
                <ZoomOut />
              </IconButton>

              <Typography
                sx={{
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  minWidth: "60px",
                  justifyContent: "center",
                }}
              >
                {Math.round(zoom * 100)}%
              </Typography>

              <IconButton
                onClick={zoomIn}
                disabled={zoom >= 5}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                  "&:disabled": {
                    color: "rgba(255, 255, 255, 0.3)",
                  },
                }}
              >
                <ZoomIn />
              </IconButton>
            </Box>
          )}

          {/* Image Counter */}
          {totalItems > 1 && (
            <Typography
              sx={{
                position: "absolute",
                top: 20,
                left: 20,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                px: 2,
                py: 1,
                borderRadius: 1,
                zIndex: 10001,
              }}
            >
              {currentIndex + 1} / {totalItems}
            </Typography>
          )}

          {/* Image Container */}
          <div
            ref={containerRef}
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              backgroundColor: "transparent",
            }}
            onMouseDown={currentIsImage ? handleMouseDown : undefined}
            onMouseMove={currentIsImage ? handleMouseMove : undefined}
            onMouseUp={currentIsImage ? handleMouseUp : undefined}
            onMouseLeave={currentIsImage ? handleMouseUp : undefined}
            onWheel={currentIsImage ? handleWheel : undefined}
            onTouchStart={currentIsImage ? handleTouchStart : undefined}
            onTouchMove={currentIsImage ? handleTouchMove : undefined}
            onTouchEnd={currentIsImage ? handleTouchEnd : undefined}
          >
            {currentIsVideo ? (
              isVideoLoading ? (
                <Box
                  sx={{
                    width: "90vw",
                    maxWidth: 960,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    color: "white",
                  }}
                >
                  {thumbnailUrl && (
                    <img
                      src={thumbnailUrl}
                      alt={currentItem?.name}
                      style={{
                        width: "100%",
                        maxHeight: "60vh",
                        objectFit: "contain",
                        borderRadius: 8,
                        opacity: 0.35,
                      }}
                    />
                  )}
                  <CircularProgress size={36} color="inherit" />
                </Box>
              ) : (
                <motion.video
                  key={`${currentItem?.path_lower}-video`}
                  controls
                  autoPlay
                  playsInline
                  muted
                  loop={true}
                  onEnded={(event) => {
                    event.currentTarget.pause();
                    event.currentTarget.currentTime = 0;
                  }}
                  poster={thumbnailUrl}
                  src={fullResUrl}
                  initial={{ opacity: 0.3 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    width: "90vw",
                    maxWidth: 1080,
                    maxHeight: "85vh",
                    borderRadius: 12,
                    backgroundColor: "#000",
                  }}
                />
              )
            ) : isImageLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <CircularProgress size={40} color="inherit" />
              </Box>
            ) : (
              <div
                style={{
                  position: "relative",
                  maxWidth: zoom > 1 ? "none" : "90%",
                  maxHeight: zoom > 1 ? "none" : "90%",
                  width: zoom > 1 ? `${zoom * 100}vw` : "90vw",
                  height: zoom > 1 ? `${zoom * 100}vh` : "90vh",
                  transform: `translate(${pan.x}px, ${pan.y}px)`,
                }}
              >
                <motion.img
                  key={`${currentItem?.path_lower}-thumb`}
                  src={thumbnailUrl}
                  alt={currentItem?.name}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{
                    opacity: shouldShowThumbnail ? 1 : 0,
                    scale: 1,
                  }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0 }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    userSelect: "none",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                  draggable={false}
                />

                {fullResUrl && (
                  <motion.img
                    ref={imageRef}
                    key={`${currentItem?.path_lower}-highres`}
                    src={fullResUrl}
                    alt={currentItem?.name}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: shouldShowHighRes ? 1 : 0 }}
                    transition={{ duration: 0 }}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      userSelect: "none",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                    draggable={false}
                  />
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StoryboardLightbox;
